import {
    RiCalendar2Line,
    RiChat1Line,
    RiComputerLine,
    RiDeleteBinLine, RiDownload2Line, RiEdit2Line, RiEmotion2Line,
    RiFileCopyLine, RiFileList2Line, RiFlashlightLine, RiLayout6Line, RiMailLine,
} from "@remixicon/react";
import React, {
    ReactElement,
    useEffect, useMemo, useState,
} from "react";
import {
    ActionContextType,
    ConversationSourceType,
    FormField,
    GetRequestTypesDocument, GetWorkflowsDocument, useCloneFormMutation,
    useDeleteWorkflowMutation,
    useGetWorkflowByIdQuery,
    useGetWorkflowsQuery,
    useSaveFormMutation,
    Workflow,
    WorkflowTriggerType,
} from "../../../../__generated__/api";
import SettingsContainer from "../../../../components/theme/settings/SettingsContainer";

import useDeleteConfirm from "../../../../components/confirm/Delete";
import ChannelTags from "../../../../components/pickers/channel-tags/ChannelTags";
import ConversationSources from "../../../../components/pickers/conversation-sources/ConversationSources";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import EmailAliases from "../../../../components/pickers/email-aliases/EmailAliases";
import { useFlatFields } from "../../../../components/pickers/field-selector/FieldSelector";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select, { Option } from "../../../../components/select/Select";
import { TemplateItem, useTemplate } from "../../../../components/template/Template";
import Badge from "../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../components/theme/drawer/Drawer";
import createFilterList from "../../../../components/theme/filter-list/FilterList";
import AutomationFlow from "../../../../components/workflow-v3/AutomationFlow";
import { ACTION_TYPE_MAP } from "../../../../components/workflow-v3/workflow/nodes";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import createTableContainer from "../../../../components/theme/table";
import Time from "../../../../components/time/Time";
import useRouting, { useParameterFilters } from "../../../../contexts/Routing";
import { ROLE_MAP } from "../../../../job-function-roles";
import { ICON_MAP } from "../request-types/RequestTypesTable";
import { isDateInRange, parseFilterDates, tryParseDate } from "../../../../date";

const TableContainer = createTableContainer<Workflow>();

const WORKFLOW_TRIGGER_TYPES = {
    [WorkflowTriggerType.Action]: {
        value: WorkflowTriggerType.Action,
        badge: <Badge variant="gray" prefixIcon={<RiFlashlightLine />}>Action</Badge>,
        text: "Action",
        icon: <RiFlashlightLine />,
        label: (
            <div>
                <Icon icon={<RiFlashlightLine />} variant="secondary" />
                {" "}
                Action
            </div>
        ),
    },
    [WorkflowTriggerType.ChannelTag]: {
        value: WorkflowTriggerType.ChannelTag,
        badge: <Badge variant="gray" prefixIcon={<Icon.ChannelTag />}>Channel Group</Badge>,
        text: "Channel Group",
        icon: <Icon.ChannelTag />,
        label: (
            <div>
                <Icon.ChannelTag variant="secondary" />
                {" "}
                Channel Group
            </div>
        ),
    },
    [WorkflowTriggerType.Email]: {
        value: WorkflowTriggerType.Email,
        badge: <Badge variant="gray" prefixIcon={<RiMailLine />}>Email</Badge>,
        text: "Email",
        icon: <RiMailLine />,
        label: (
            <div>
                <Icon icon={<RiMailLine />} variant="secondary" />
                {" "}
                Email
            </div>
        ),
    },
    [WorkflowTriggerType.Emoji]: {
        value: WorkflowTriggerType.Emoji,
        badge: <Badge variant="gray" prefixIcon={<RiEmotion2Line />}>Emoji</Badge>,
        text: "Emoji",
        icon: <RiEmotion2Line />,
        label: (
            <div>
                <Icon icon={<RiEmotion2Line />} variant="secondary" />
                {" "}
                Emoji
            </div>
        ),
    },
    [WorkflowTriggerType.InboundWebhook]: {
        value: WorkflowTriggerType.InboundWebhook,
        badge: <Badge variant="gray" prefixIcon={<RiDownload2Line />}>Webhook</Badge>,
        text: "Webhook",
        icon: <RiDownload2Line />,
        label: (
            <div>
                <Icon icon={<RiDownload2Line />} variant="secondary" />
                {" "}
                Webhook
            </div>
        ),
    },
    [WorkflowTriggerType.RequestType]: {
        value: WorkflowTriggerType.RequestType,
        badge: <Badge variant="gray" prefixIcon={<RiFileList2Line />}>Request Type</Badge>,
        text: "Request Type",
        icon: <RiFileList2Line />,
        label: (
            <div>
                <Icon icon={<RiFileList2Line />} variant="secondary" />
                {" "}
                Request Type
            </div>
        ),
    },
    [WorkflowTriggerType.WebChat]: {
        value: WorkflowTriggerType.WebChat,
        badge: <Badge variant="gray" prefixIcon={<RiChat1Line />}>Web Chat</Badge>,
        text: "Web Chat",
        icon: <RiChat1Line />,
        label: (
            <div>
                <Icon icon={<RiChat1Line />} variant="secondary" />
                {" "}
                Web Chat
            </div>
        ),
    },
    [WorkflowTriggerType.WorkspaceAction]: {
        value: WorkflowTriggerType.WorkspaceAction,
        badge: <Badge variant="gray" prefixIcon={<RiComputerLine />}>System</Badge>,
        text: "System",
        icon: <RiComputerLine />,
        label: (
            <div>
                <Icon icon={<RiComputerLine />} variant="secondary" />
                {" "}
                System
            </div>
        ),
    },
    [WorkflowTriggerType.Integration]: {
        value: WorkflowTriggerType.Integration,
        badge: <Badge variant="gray" prefixIcon={<Icon.Integrations />}>Integration</Badge>,
        text: "Integration",
        icon: <Icon.Integrations />,
        label: undefined,
    },

};

const WORKFLOW_TRIGGER_TYPES_OPTIONS = Object.values(WORKFLOW_TRIGGER_TYPES).filter(row => row.label != null);

const FilterList = createFilterList<WorkflowTriggerType | "all">();

function useCreateView() {
    const [view, setView] = useState<Workflow | undefined>(undefined);

    const [saveForm] = useSaveFormMutation();
    const { Paths, historyPush } = useRouting();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: view?.id ? "Edit Automation" : "Add Automation",
        onHide: () => {
            setView(undefined);
            formTracker.reset();
        },
        onSave: async () => {
            // const result = await saveForm({
            //     refetchQueries: [
            //         { query: GetRequestTypesDocument, variables: { type: FormType.Action } },
            //         { query: GetFormDataDocument },
            //     ],
            //     variables: {
            //         id: view?.id,
            //         form: {
            //             name: formTracker.properties.name.value,
            //             description: formTracker.properties.description.value,
            //             type: formTracker.properties.type.value,
            //             fields: [],
            //         },
            //     },
            // });

            // if editing don't redirect to the automation page
            // const newId = result.data?.saveForm?.id;
            // if (newId && !view) {
            //     historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
            //         setting: "workflow-actions",
            //         id: newId,
            //     });
            // }

            setView(undefined);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property("name", () => view?.name ?? "")
                .property("description", () => view?.description ?? "")
                .property<"requestTypes", string[]>("requestTypes", () => [])
                .property<"channelTags", string[]>("channelTags", () => [])
                .property<"emails", string[]>("emails", () => [])
                .property<"conversationSources", string[]>("conversationSources", () => [])
                .property("type", () => view?.triggers?.firstElement.type ?? WorkflowTriggerType.Action),
            dependencies: [],
        },
        body: tracker => (
            <>
                <FormGroup
                    label="Automation Type"
                    size="medium"
                >
                    <Select
                        options={WORKFLOW_TRIGGER_TYPES_OPTIONS as Option<unknown>[]}
                        onChange={selected => {
                            tracker.properties.type.setValue(selected.value as WorkflowTriggerType);
                        }}
                        value={{
                            value: tracker.properties.type.value,
                            label: WORKFLOW_TRIGGER_TYPES[
                                tracker.properties.type.value as keyof typeof WORKFLOW_TRIGGER_TYPES
                            ].label as ReactElement,
                        }}
                    />
                </FormGroup>

                {tracker.properties.type.value === WorkflowTriggerType.RequestType && (
                    <FormGroup
                        label="Select Request Type to link with"
                        size="medium"
                        required
                    >
                        <RequestTypes
                            onSelected={tracker.properties.requestTypes.setValue}
                            value={tracker.properties.requestTypes.value}
                            isMulti
                        />
                    </FormGroup>
                )}

                {tracker.properties.type.value === WorkflowTriggerType.ChannelTag && (
                    <FormGroup
                        label="Channel Groups"
                        size="medium"
                        required
                    >
                        <ChannelTags
                            onSelected={tracker.properties.channelTags.setValue}
                            value={tracker.properties.channelTags.value}
                            isMulti
                        />
                    </FormGroup>
                )}

                {tracker.properties.type.value === WorkflowTriggerType.Email && (
                    <FormGroup
                        label="Emails"
                        size="medium"
                        required
                    >
                        <EmailAliases
                            onSelected={tracker.properties.emails.setValue}
                            value={tracker.properties.emails.value}
                            isMulti
                        />
                    </FormGroup>
                )}

                {tracker.properties.type.value === WorkflowTriggerType.Emoji && (
                    <FormGroup
                        label="Emoji Channels"
                        size="medium"
                        required
                    >
                        <ConversationSources
                            onSelected={tracker.properties.conversationSources.setValue}
                            value={tracker.properties.conversationSources.value}
                            isMulti
                            sourceTypes={[ConversationSourceType.ConversationGroup, ConversationSourceType.SlackChannel]}
                        />
                    </FormGroup>
                )}

                <FormGroup
                    label="Automation Name"
                    placeholder="Enter Automation Name"
                    size="medium"
                    required
                    value={tracker.properties.name.value}
                    onChange={evt => tracker.properties.name.setValue(evt.target.value)}
                />
                <FormGroup
                    label="Description"
                    placeholder="Enter Automation Description"
                    size="medium"
                    display="textarea"
                    isOptional
                    value={tracker.properties.description.value}
                    onChange={evt => tracker.properties.description.setValue(evt.target.value)}
                />
            </>
        ),
    });

    useEffect(() => {
        if (view) {
            setOpen(true);
        }
    }, [view]);

    return { element, setOpen, setView };
}

function useViewAutomation() {
    const [workflowId, setWorkflowId] = useState<string | undefined>(undefined);
    const { data: workflow } = useGetWorkflowByIdQuery({
        skip: !workflowId,
        variables: {
            id: workflowId,
        },
    });

    const { flatFields } = useFlatFields(undefined, {
        shouldShowAllFields: true,
    });

    const actionsContextType = useMemo(
        () => ACTION_TYPE_MAP[workflow?.workflow?.steps?.firstElement?.actionId] ?? ActionContextType.GenericNoTicket,
        [workflow],
    );

    const workflowTitle = useMemo(() => {
        const trigger = workflow?.workflow?.triggers?.firstElement;
        if (!trigger) {
            return workflow?.workflow?.name;
        }
        return `${WORKFLOW_TRIGGER_TYPES[trigger.type].text}: ${trigger.text}`;
    }, [workflow]);
    return {
        element: workflowId && workflow?.workflow ? (
            <AutomationFlow
                onSaved={() => {
                    // do nothing for now.
                }}
                title={workflowTitle as string}
                workflow={workflow.workflow as Workflow}
                onHide={() => {
                    setWorkflowId(undefined);
                }}
                formFields={Object.values(flatFields).map(field => field.field) as FormField[]}
                actionsContextType={actionsContextType}
            />
        ) : undefined,
        setWorkflowId,
    };
}
export default function ActionsV3() {
    const { data, error, loading: isLoading } = useGetWorkflowsQuery();
    const [cloneForm] = useCloneFormMutation();

    const { historyPush, Paths } = useRouting();

    const { element: drawerElement, setOpen, setView } = useCreateView();

    const [filters, setFilters] = useParameterFilters(Paths.ADMINISTRATOR_SETTING, {
        source: "all",
    });

    const [deleteWorkflow] = useDeleteWorkflowMutation();

    const { element: viewAutomation, setWorkflowId: setViewWorkflowId } = useViewAutomation();

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Workflow>(
        async group => {
            await deleteWorkflow({
                refetchQueries: [
                    { query: GetWorkflowsDocument },
                ],
                variables: {
                    id: group.id,
                },
            });
        },
        user => user.name,
        "Delete Automation",
        "Do you want to delete",
        "Remove",
    );

    const templates = useMemo(() => data?.templates.map(template => {
        const JobIcon = template.jobFunctions?.isNotEmpty ? ROLE_MAP[template.jobFunctions?.firstElement]?.icon : undefined;
        return ({
            id: template.id,
            title: template.name,
            description: template.description,
            categories: template.jobFunctions?.mapFilter(jobFunction => ROLE_MAP[jobFunction]?.title),
            icon: JobIcon ? <JobIcon /> : undefined,
        } as TemplateItem);
    }) ?? [], [data?.templates]);

    const { displayItems, groupOptions } = useMemo(
        () => ({
            displayItems: data?.workflows?.filter(row => !filters.source
                || filters.source === "all"
                || row.triggers?.some(trigger => trigger.type === filters.source)),
            groupOptions: data?.workflows?.distribution(trigger => trigger.triggers?.firstElement?.type as string) ?? {},
        }),
        [filters.source, data?.workflows],
    );

    const { element: templateElement, onShow: onShowTemplate, onHide: onHideTemplate } = useTemplate({
        templates,
        title: "Add Automation",
        categoryIcons: ICON_MAP,
        categoryIcon: <RiLayout6Line />,
        onUseTemplate: async template => {
            const created = await cloneForm({
                refetchQueries: [GetRequestTypesDocument],
                variables: {
                    id: template.id,
                },
            });
            historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                id: created.data?.cloneForm.id,
                setting: "workflow-actions",
            });
        },
        onCreate: () => {
            onHideTemplate();
            setOpen(true);
        },
    });

    const handleAddAutomation = () => {
        onShowTemplate();
    };

    return (
        <SettingsContainer navigation="none">
            <SettingsContainer.Section title="Automations" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    title="Automations"
                    description="Manage the automations users see in their dashboards."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isFullWidth
                >
                    <TableContainer data={displayItems as Workflow[] ?? []} isLoading={isLoading} error={error}>
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="Automations are used to automate tasks in your workflows."
                                title="Automations"
                                icon={<RiFlashlightLine />}
                                onClick={handleAddAutomation}
                                buttonText="Add Automation"
                            />
                        </TableContainer.Empty>
                        <TableContainer.Header>
                            <FilterList
                                selectedItem={filters.source as WorkflowTriggerType ?? "all"}
                                onChangeSelected={key => setFilters("source", key)}
                            >
                                <FilterList.Button filterItem="all" text="All" count={data?.workflows?.length} />
                                {Object.entries(groupOptions).map(([type, count]) => {
                                    const source = WORKFLOW_TRIGGER_TYPES[type as WorkflowTriggerType];
                                    return source ? (
                                        <FilterList.Button
                                            filterItem={type as WorkflowTriggerType}
                                            text={source.text}
                                            count={count}
                                            key={type}
                                            // prefixIcon={source.icon}
                                        />
                                    ) : undefined;
                                })}
                            </FilterList>
                        </TableContainer.Header>
                        <TableContainer.TableControls>

                            <TableContainer.TableControls.Search
                                search={(query, group) => group.name?.toLocaleLowerCase().includes(query)
                                    || group.description?.toLocaleLowerCase().includes(query)
                                    || group.triggers?.some(row => row.type.toLocaleLowerCase().includes(query)
                                        || row.text?.toLocaleLowerCase().includes(query))}
                            />
                            <TableContainer.TableControls.Seperator />

                            <TableContainer.TableControls.Filter
                                defaultValue={{
                                    startDate: undefined as Date | undefined,
                                    endDate: undefined as Date | undefined,
                                }}
                                deserialize={keys => (typeof keys === "object" && !Array.isArray(keys) ? ({
                                    startDate: keys.startDate ? new Date(keys.startDate) : undefined,
                                    endDate: keys.endDate ? new Date(keys.endDate) : undefined,
                                }) : undefined)}
                                serialize={value => ({
                                    startDate: value.startDate?.toISOString(),
                                    endDate: value.endDate?.toISOString(),
                                })}
                                filterKey="modifiedDate"
                                onFilter={(item, currentValue) => isDateInRange(tryParseDate(item.modifiedDate), currentValue)}
                            >
                                {(currentValue, setValue) => (
                                    <DateRangeCalendarPicker
                                        toggleText="Modified"
                                        icon={<RiCalendar2Line />}
                                        value={{
                                            startDate: currentValue.startDate,
                                            endDate: currentValue.endDate,
                                        }}
                                        onChange={change => {
                                            const dates = parseFilterDates(change);
                                            setValue({
                                                startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                                endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                            });
                                        }}
                                    />
                                )}

                            </TableContainer.TableControls.Filter>
                            <TableContainer.TableControls.Filters shouldWriteUrl />

                            <TableContainer.TableControls.SortBy />
                            <TableContainer.TableControls.Create
                                text="Add Automation"
                                onClick={handleAddAutomation}
                            />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="name"
                                title="Name"
                                sortBy={item => item.name?.toLowerCase()}
                            >
                                {item => item.name}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="description"
                                title="Description"
                                sortBy={item => item.description?.toLowerCase()}
                            >
                                {item => item.description}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="triggerType"
                                title="Type"
                                sortBy={item => item.triggers?.firstElement?.type}
                            >
                                {item => WORKFLOW_TRIGGER_TYPES[item.triggers?.firstElement?.type as WorkflowTriggerType]?.badge}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="linkedTo"
                                title="Linked To"
                                sortBy={item => item.triggers?.firstElement?.text}
                            >
                                {item => <Badge variant="primary">{item.triggers?.firstElement?.text}</Badge>}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="modified"
                                title="Last Modified"
                                sortBy={item => item.modifiedDate?.toLocaleString()}
                            >
                                {item => <Time date={item.modifiedDate} variant="date-time" />}
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<RiFileCopyLine />}
                                altText="Clone Automation"
                                // onClick={column => setView(column)}
                            />
                            <TableContainer.Action
                                icon={<RiEdit2Line />}
                                altText="Edit Automation"
                                onClick={workflow => {
                                    setViewWorkflowId(workflow.id);
                                }}
                            />
                            <TableContainer.Action
                                icon={<RiDeleteBinLine />}
                                altText="Delete Automation"
                                onClick={row => setDeleting(row)}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                    {deleteElement}
                    {drawerElement}
                    {templateElement}
                    {viewAutomation}
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
        </SettingsContainer>

    );
}
